import { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { reduxForm } from 'redux-form';
import noop from 'lodash/noop';
import flow from 'lodash/flow';
import get from 'lodash/get';
import paymentFlows from '../../constants/payment/paymentFlows';
import { addPaymentErrors } from '../../utils/form/paymentValidation';
import isPaymentRequired from '../utils/isPaymentRequired';
import connectWithExperiments from '../../decorators/connectWithExperiments';
import {
  SUBSCRIBE_PAYMENT_FLOW_FORM_LAYOUT_HEADER,
  SUBSCRIBE_PAYMENT_FLOW_FORM_LAYOUT_SUB_HEADER,
} from '../../constants/localizations/payment';
import { SUBSCRIBE_SUBMIT_BUTTON } from '../../constants/localizations/subscription';
import { AUTH_PAYMENT_FORM_PREVIEW_ENABLED } from '../../constants/experiments/auth';
import AuthAndPaymentFlowHeader from '../shared/AuthAndPaymentFlowHeader';
import { LocationAndLocalizationContext } from '../../providers/LocationAndLocalizationProvider';
import css from './fakePaymentForm.module.scss';
import AutoRenewCopy from './AutoRenewCopy';
import PaymentOptionsWrapper from './PaymentOptionsWrapper';

const FORM_ID = 'fakeSubscribeForm';

// exported for testing
export class FakePaymentForm extends Component {
  static propTypes = {
    breakpoint: PropTypes.number.isRequired,
    products: PropTypes.array.isRequired,
    [AUTH_PAYMENT_FORM_PREVIEW_ENABLED]: PropTypes.bool,
    selectedProduct: PropTypes.object,
    isRecurlyLoading: PropTypes.bool.isRequired,
    setRecurlyLoadingState: PropTypes.func.isRequired,
  };

  static contextType = LocationAndLocalizationContext;

  constructor(props, context) {
    super(props);
    const { location } = context;

    const isNoPaymentFlow = !isPaymentRequired(props.selectedProduct, location);

    this.state = {
      isNoPaymentFlow,
      hasRecurlyError: false,
    };
    this.setRecurlyErrorState = this.setRecurlyErrorState.bind(this);
  }

  setRecurlyErrorState(hasError) {
    if (hasError !== this.state.hasRecurlyError) {
      this.setState({
        hasRecurlyError: hasError,
      });
    }
  }

  render() {
    const {
      breakpoint,
      products,
      [AUTH_PAYMENT_FORM_PREVIEW_ENABLED]: isPaymentPreviewEnabled,
      selectedProduct,
      setRecurlyLoadingState,
      isRecurlyLoading,
    } = this.props;

    const { isNoPaymentFlow, hasRecurlyError } = this.state;

    const shouldShowFakePaymentForm =
      isPaymentPreviewEnabled && !isNoPaymentFlow;
    if (!shouldShowFakePaymentForm) {
      setRecurlyLoadingState(false);
      return null;
    }

    if (hasRecurlyError) {
      return null;
    }

    const selectedProductSku = get(selectedProduct, 'sku');

    const paymentProps = {
      selectedProductSku,
      breakpoint,
      products,
      formId: FORM_ID,
      isFormDisabled: false,
      disableCreditCardForm: noop,
      onSubmitSuccess: noop,
      onSubmitFailure: noop,
      openFailureDialog: noop,
      handlePaymentFlowChange: noop,
      selectedProduct,
      setSelectedProductSku: noop,
      currentPaymentFlow: paymentFlows.creditCard,
      actions: {
        logSubscribeActivity: noop,
      },
      defaultSubmitButtonLabel: SUBSCRIBE_SUBMIT_BUTTON,
      onSubmit: noop,
      hideFooterCopy: true,
      setRecurlyErrorState: this.setRecurlyErrorState,
      setRecurlyLoadingState,
    };

    const headerProps = {
      header: SUBSCRIBE_PAYMENT_FLOW_FORM_LAYOUT_HEADER,
      subheader: SUBSCRIBE_PAYMENT_FLOW_FORM_LAYOUT_SUB_HEADER,
    };

    return (
      <div className={css.fakePaymentFormStyle}>
        <div className={css.overlayStyle} data-testid="paymentFormOverlay" />
        {!isRecurlyLoading && <AuthAndPaymentFlowHeader {...headerProps} />}
        <PaymentOptionsWrapper {...paymentProps} {...this.props} />
        {!isRecurlyLoading && <AutoRenewCopy />}
      </div>
    );
  }
}

export function mapStateToProps(state) {
  return {
    form: state.form[FORM_ID],
  };
}

export default flow(
  connect(mapStateToProps),
  reduxForm({
    form: FORM_ID,
    validate: addPaymentErrors,
  }),
  connectWithExperiments([AUTH_PAYMENT_FORM_PREVIEW_ENABLED]),
)(FakePaymentForm);
